import React, {CSSProperties, MouseEvent, ReactNode, createContext, useContext, useState} from 'react'

import {focusableClass, ensureFocusStyles} from '../styles.js'
import {navActiveColor, sideNavColors, sideNavSizes} from './styles.js'

ensureFocusStyles()

interface SideNavContextValue {
    /** The rail is expanded: the labels show */
    expanded: boolean
    branding?: {color?: string} | null
    history?: {push: (path: string) => void}
    /** A menu (Popover) opened from the rail keeps it expanded until it closes, pass this as its onToggle */
    onMenuToggle: (open: boolean) => void
}

export const SideNavContext = createContext<SideNavContextValue>({expanded: true, onMenuToggle: () => {}})

/** The state of the SideNav around it, for things rendered in it (the branch selector) */
export const useSideNav = () => useContext(SideNavContext)

interface SideNavItemProps {
    /** Icon class, e.g. 'mdi mdi-map' */
    icon?: string
    /** Shown in the icon column instead of the icon (the avatar of the account) */
    iconNode?: ReactNode
    title: ReactNode
    subTitle?: ReactNode
    /** Path to navigate to: the row is a link, so it can also be opened in a new tab */
    target?: string
    /** Called instead of navigating. Without target and onClick the row is a button that does nothing itself, e.g.
     * the anchor of a Popover that opens on its click. */
    onClick?: () => void
    active?: boolean
    /** Shown at the right while the rail is expanded (e.g. the chevron of a menu) */
    trailing?: ReactNode
    /** Opens a menu (the anchor of a Popover) */
    hasPopup?: boolean
}

/**
 * One row of the SideNav: a fixed icon column, so the icons don't move when the rail expands, and the label that fades
 * in while it is expanded. The active row has a light background and a bar in the branding colour at its left.
 */
export default function SideNavItem({icon, iconNode, title, subTitle, target, onClick, active, trailing, hasPopup}: SideNavItemProps) {
    const {expanded, branding, history} = useContext(SideNavContext)
    const [hover, setHover] = useState(false)

    const height = sideNavSizes.rowHeight

    const onClickRow = (event: MouseEvent) => {
        if (onClick) {
            event.preventDefault()
            onClick()
        } else if (target) {
            // Let the browser open the link in a new tab or window
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
                return
            }

            event.preventDefault()
            history?.push(target)
        }
    }

    const style: CSSProperties = {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        width: '100%',
        height,
        padding: 0,
        boxSizing: 'border-box',
        border: 'none',
        borderRadius: sideNavSizes.radius,
        background: active ? sideNavColors.active : hover ? sideNavColors.hover : 'transparent',
        color: active || hover ? sideNavColors.textActive : sideNavColors.text,
        font: 'inherit',
        textAlign: 'left',
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'background .15s ease, color .15s ease'
    }

    const labelStyle: CSSProperties = {
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        opacity: expanded ? 1 : 0,
        transition: 'opacity .15s ease'
    }

    const content = (
        <>
            {active &&
                <span style={{position: 'absolute', left: -sideNavSizes.padding, top: 10, bottom: 10, width: sideNavSizes.indicatorWidth, borderRadius: 2, background: navActiveColor(branding)}} />
            }

            <span
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: sideNavSizes.iconColumn,
                    flexShrink: 0,
                    fontSize: sideNavSizes.iconSize
                }}
            >
                {iconNode || (icon && <i className={icon} />)}
            </span>

            <span style={labelStyle}>
                <span style={{display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: sideNavSizes.fontSize, fontWeight: active ? 600 : 500}}>{title}</span>
                {subTitle &&
                    <span style={{display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: sideNavSizes.subTitleFontSize, color: sideNavColors.text}}>{subTitle}</span>
                }
            </span>

            {trailing &&
                <span style={{display: 'flex', alignItems: 'center', paddingRight: 8, fontSize: 18, opacity: expanded ? 1 : 0, transition: 'opacity .15s ease'}}>
                    {trailing}
                </span>
            }
        </>
    )

    const common = {
        className: focusableClass,
        style,
        onClick: onClickRow,
        onMouseEnter: () => setHover(true),
        onMouseLeave: () => setHover(false)
    }

    if (target && !onClick) {
        return (
            <a {...common} href={target} aria-current={active ? 'page' : undefined}>
                {content}
            </a>
        )
    }

    return (
        <button {...common} type='button' aria-haspopup={hasPopup ? 'menu' : undefined}>
            {content}
        </button>
    )
}
